import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Heart, RotateCcw } from "lucide-react";
import { usePaymentStatus } from "@/hooks/usePaymentStatus";
import { Logo } from "@/components/Logo";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-ink text-slate-50 flex flex-col items-center justify-center px-5 text-center" data-testid="payment-page">
    <div className="grain" aria-hidden="true" />
    <Link to="/" className="flex items-center gap-2.5 mb-10" data-testid="payment-back-home-logo">
      <Logo size={34} />
      <span className="font-display text-xl tracking-wide">PEPE <span className="text-cyan-400">AVS</span></span>
    </Link>
    {children}
    <Link
      to="/"
      data-testid="payment-back-home-button"
      className="mt-10 px-7 py-4 rounded-full border border-line text-sm uppercase tracking-wide font-bold text-slate-200 hover:border-cyan-400/60 hover:text-cyan-300 transition-colors"
    >
      Retour au site
    </Link>
  </div>
);

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { state, livemode, retry } = usePaymentStatus(sessionId);

  return (
    <Shell>
      {state === "pending" && (
        <div data-testid="payment-status-pending">
          <Clock size={40} className="mx-auto text-amber-300 animate-pulse" />
          <h1 className="font-display uppercase text-4xl sm:text-5xl mt-6">Vérification…</h1>
          <p className="mt-3 text-slate-400">On confirme ton paiement avec Stripe.</p>
        </div>
      )}
      {state === "paid" && (
        <div data-testid="payment-status-paid">
          <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
          <h1 className="font-display uppercase text-4xl sm:text-6xl mt-6">
            Merci <Heart size={30} className="inline text-amber-300 fill-amber-300 -mt-2" />
          </h1>
          <p className="mt-4 text-slate-300 text-lg max-w-md mx-auto" data-testid="payment-confirmation-message">
            {livemode === false ? "Paiement de test validé : aucun argent réel n'a été débité ni reçu." : "Ton paiement est confirmé par Stripe. Pépé te remercie !"}
          </p>
        </div>
      )}
      {state === "timeout" && (
        <div data-testid="payment-status-timeout">
          <Clock size={40} className="mx-auto text-amber-300" />
          <h1 className="font-display uppercase text-4xl mt-6">Encore un instant</h1>
          <p className="mt-3 text-slate-400 max-w-md mx-auto">
            Le paiement n'est pas encore confirmé. Vérifie ton compte avant de recommencer pour éviter un double paiement.
          </p>
        </div>
      )}
      {state === "error" && (
        <div data-testid="payment-status-error">
          <XCircle size={40} className="mx-auto text-red-400" />
          <h1 className="font-display uppercase text-4xl mt-6">Paiement non confirmé</h1>
          <p className="mt-3 text-slate-400 max-w-md mx-auto">Nous ne pouvons pas confirmer ce paiement. Vérifie ton compte ou ton reçu Stripe avant de recommencer.</p>
        </div>
      )}
      {state === "refunded" && <div data-testid="payment-status-refunded"><RotateCcw size={40} className="mx-auto text-amber-300" /><h1 className="font-display uppercase text-4xl mt-6">Remboursement enregistré</h1><p className="mt-3 text-slate-400 max-w-md">Consulte ton reçu Stripe pour le montant et le délai de remboursement.</p></div>}
      {["error", "timeout"].includes(state) && sessionId && <button onClick={retry} data-testid="payment-retry-button" className="mt-6 inline-flex gap-2 items-center text-amber-300 underline"><RotateCcw size={16} />Vérifier à nouveau</button>}
    </Shell>
  );
}

export function PaymentCancelPage() {
  return (
    <Shell>
      <div data-testid="payment-status-cancelled">
        <XCircle size={40} className="mx-auto text-slate-400" />
        <h1 className="font-display uppercase text-4xl sm:text-5xl mt-6">Paiement annulé</h1>
        <p className="mt-3 text-slate-400 max-w-md mx-auto">Le paiement n'a pas été finalisé sur cette page. Si tu avais déjà confirmé ailleurs, vérifie ton compte avant de recommencer.</p>
      </div>
    </Shell>
  );
}
